/**
 * HistoryPage — Phase 6a.19 per Frontend Spec §21.10.
 *
 * Multi-engagement workflow surface. Lists every non-deleted engagement
 * persisted via `LocalStorageHistoryStorage` (Phase 6a.18a) as a card
 * with audit-period metadata + status badge + 4 actions:
 *
 *   • Aç       — navigate to the engagement results page (or fall back
 *                to the contract-list edit page if never calculated)
 *   • Düzenle  — navigate to the contract-list edit page
 *   • Çoğalt   — deep-copy with a fresh id, clear lastResult/isStale,
 *                save + refresh + toast
 *   • Sil      — confirmation dialog → soft-delete via service →
 *                10-second sonner undo toast that restores the entry
 *
 * Toolbar provides search (clientName / id substring), status filter
 * (Tümü / Taslak / Hesaplanmış / Güncel Değil), currency filter
 * (dynamic from data), sort (newest / oldest / clientName), and a
 * conditional Clear Filters button.
 *
 * On mount: `listEngagements()` populates the cards and
 * `purgeOldDeleted()` runs fire-and-forget to keep localStorage healthy
 * (entries soft-deleted > 30 days ago are hard-removed by the service).
 */

import { format, parseISO } from "date-fns";
import { Copy, Search, Share2, Trash2, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShareEngagementDialog } from "@/features/engagement/components/ShareEngagementDialog";
import { StatusBadge } from "@/features/engagement/components/StatusBadge";
import { deriveEngagementStatus } from "@/features/engagement/utils/deriveEngagementStatus";
import { useHistoryStorage } from "@/services";
import type {
  EngagementHistoryEntry,
  EngagementStatus,
  EngagementWithAccess,
} from "@/types";

type StatusFilter = EngagementStatus | "ALL";
type SortOrder = "newest" | "oldest" | "clientName";

const UNDO_DURATION_MS = 10_000;

function isFiltered(
  searchQuery: string,
  statusFilter: StatusFilter,
  currencyFilter: string,
): boolean {
  return (
    searchQuery !== "" ||
    statusFilter !== "ALL" ||
    currencyFilter !== "ALL"
  );
}

export function HistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storage = useHistoryStorage();

  const [loaded, setLoaded] = React.useState<
    EngagementWithAccess[] | null
  >(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("ALL");
  const [currencyFilter, setCurrencyFilter] = React.useState<string>("ALL");
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("newest");
  const [deleteCandidate, setDeleteCandidate] =
    React.useState<EngagementHistoryEntry | null>(null);
  const [shareTarget, setShareTarget] =
    React.useState<EngagementWithAccess | null>(null);

  const refresh = React.useCallback(async () => {
    try {
      const entries = await storage.listEngagements();
      setLoaded(entries);
    } catch (err) {
      console.error("[history] listEngagements failed", err);
      toast.error(t("history.loadError"));
      setLoaded([]);
    }
  }, [storage, t]);

  React.useEffect(() => {
    void refresh();
    // Phase 6a.19 §21.10.9 — fire-and-forget purge of soft-deleted
    // entries past the 30-day TTL. Service logs telemetry internally;
    // no UI surface.
    storage
      .purgeOldDeleted()
      .then((count) => {
        if (count > 0) {
          console.log(`[history] purged ${count} old soft-deleted entries`);
        }
      })
      .catch((err) => console.error("[history] purgeOldDeleted failed", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uniqueCurrencies = React.useMemo(() => {
    if (!loaded) return [] as string[];
    return Array.from(new Set(loaded.map((e) => e.functionalCurrency))).sort();
  }, [loaded]);

  const filtered = React.useMemo(() => {
    if (!loaded) return [] as EngagementWithAccess[];
    const q = searchQuery.trim().toLowerCase();
    return loaded.filter((e) => {
      if (q) {
        const inName = e.clientName.toLowerCase().includes(q);
        const inId = e.id.toLowerCase().includes(q);
        if (!inName && !inId) return false;
      }
      if (statusFilter !== "ALL") {
        if (deriveEngagementStatus(e) !== statusFilter) return false;
      }
      if (currencyFilter !== "ALL") {
        if (e.functionalCurrency !== currencyFilter) return false;
      }
      return true;
    });
  }, [loaded, searchQuery, statusFilter, currencyFilter]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      switch (sortOrder) {
        case "newest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "clientName":
          return a.clientName.localeCompare(b.clientName, "tr");
      }
    });
    return arr;
  }, [filtered, sortOrder]);

  const filtersActive = isFiltered(searchQuery, statusFilter, currencyFilter);

  function clearFilters() {
    setSearchQuery("");
    setStatusFilter("ALL");
    setCurrencyFilter("ALL");
  }

  function handleOpen(engagement: EngagementHistoryEntry) {
    if (!engagement.lastResult) {
      toast.info(t("history.open.notCalculated"));
      navigate(`/engagements/${engagement.id}/contracts`);
      return;
    }
    navigate(`/engagements/${engagement.id}/results/balance-sheet`);
  }

  function handleEditInfo(engagement: EngagementHistoryEntry) {
    // Phase 6a.19a — engagement metadata edit (clientName / dates /
    // currency / opening balance mode) opens the dedicated edit page.
    navigate(`/engagements/${engagement.id}/edit`);
  }

  function handleEditContracts(engagement: EngagementHistoryEntry) {
    // Phase 6a.19a — contract-list edit (add / update / delete contracts)
    // is the original Phase 6a.10 surface.
    navigate(`/engagements/${engagement.id}/contracts`);
  }

  async function handleDuplicate(engagement: EngagementHistoryEntry) {
    const now = new Date().toISOString();
    const copy: EngagementHistoryEntry = {
      ...engagement,
      id: `eng-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      clientName: `${engagement.clientName}${t("history.duplicate.suffix")}`,
      // Deep-clone the contracts array so edits don't leak across copies.
      contracts: engagement.contracts.map((c) => (c ? { ...c } : c)),
      contractsDirty: engagement.contractsDirty
        ? [...engagement.contractsDirty]
        : undefined,
      // Force the duplicate into draft state — the engagementId
      // embedded in `lastResult` would not match the new id, so it
      // would mislead any downstream consumer. Auditor recalculates.
      lastResult: undefined,
      isStale: false,
      status: "draft",
      calculatedAt: undefined,
      staleSince: undefined,
      lastCalculateError: undefined,
      isDeleted: false,
      deletedAt: undefined,
      createdAt: now,
      updatedAt: now,
    };
    try {
      await storage.saveEngagement(copy);
      await refresh();
      toast.success(t("history.duplicate.success", { name: copy.clientName }));
    } catch (err) {
      console.error("[history] duplicate failed", err);
      toast.error(t("history.loadError"));
    }
  }

  // TEMP: cross-check input export (MINI_FIX_BQ) — remove with the dev-tools folder + this button.
  async function handleCrossCheck(engagement: EngagementHistoryEntry) {
    try {
      const { buildCrossCheckWorkbook, crossCheckFilename } = await import(
        "@/features/dev-tools/crossCheckExport"
      );
      const { saveAs } = await import("file-saver");
      const buffer = await buildCrossCheckWorkbook(engagement);
      saveAs(new Blob([buffer]), crossCheckFilename(engagement));
      toast.success("Cross-check Excel indirildi");
    } catch (err) {
      console.error("[crosscheck] export failed", err);
      const msg = (err as Error)?.message ?? "";
      // A lazy-chunk 404 after a fresh deploy (stale tab) is the most common
      // cause — the dynamic import() can't fetch the old hashed chunk. Guide
      // the user to refresh; otherwise surface the real error so it's not opaque.
      const staleChunk =
        /dynamically imported module|module script failed|Failed to fetch/i.test(
          msg,
        );
      toast.error(
        staleChunk
          ? "Uygulama güncellendi — lütfen sayfayı yenileyin (Ctrl+Shift+R) ve tekrar deneyin."
          : `Cross-check Excel oluşturulamadı: ${msg || "bilinmeyen hata"}`,
      );
    }
  }

  async function confirmDelete() {
    const target = deleteCandidate;
    if (!target) return;
    setDeleteCandidate(null);
    try {
      await storage.deleteEngagement(target.id);
      await refresh();
      // Phase 6a.19 §21.10.5 — 10-second sonner undo. Restoration
      // clears both `isDeleted` and `deletedAt` (service uses the
      // boolean for the listEngagements filter, but a stale
      // `deletedAt` would be inconsistent).
      toast(t("history.delete.success", { name: target.clientName }), {
        duration: UNDO_DURATION_MS,
        action: {
          label: t("history.delete.undo"),
          onClick: () => {
            const restored: EngagementHistoryEntry = {
              ...target,
              // Mirror deleteEngagement's reclaim: the snapshot was
              // dropped from the store on delete, so undo brings back a
              // clean DRAFT the auditor recalculates — never a stale
              // (and re-bloating) result payload.
              lastResult: undefined,
              status: "draft",
              isStale: false,
              calculatedAt: undefined,
              staleSince: undefined,
              lastCalculateError: undefined,
              isDeleted: false,
              deletedAt: undefined,
              updatedAt: new Date().toISOString(),
            };
            void storage
              .saveEngagement(restored)
              .then(() => refresh())
              .then(() =>
                toast.success(
                  t("history.delete.restored", { name: target.clientName }),
                ),
              )
              .catch((err) => {
                console.error("[history] restore failed", err);
                toast.error(t("history.loadError"));
              });
          },
        },
      });
    } catch (err) {
      console.error("[history] delete failed", err);
      toast.error(t("history.loadError"));
    }
  }

  if (loaded === null) {
    return <SkeletonPlaceholder />;
  }

  if (loaded.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="history-page">
      <Header count={sorted.length} />

      <Toolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        currencyFilter={currencyFilter}
        onCurrencyChange={setCurrencyFilter}
        uniqueCurrencies={uniqueCurrencies}
        sortOrder={sortOrder}
        onSortChange={setSortOrder}
        filtersActive={filtersActive}
        onClearFilters={clearFilters}
      />

      {sorted.length === 0 ? (
        <FilteredEmptyState />
      ) : (
        <div className="space-y-3" data-testid="history-list">
          {sorted.map((engagement) => (
            <EngagementCard
              key={engagement.id}
              engagement={engagement}
              onOpen={() => handleOpen(engagement)}
              onEditInfo={() => handleEditInfo(engagement)}
              onEditContracts={() => handleEditContracts(engagement)}
              onDuplicate={() => void handleDuplicate(engagement)}
              onDelete={() => setDeleteCandidate(engagement)}
              canShare={
                engagement._access.isOwner || engagement._access.myCanInvite
              }
              onShare={() => setShareTarget(engagement)}
              isShared={!engagement._access.isOwner}
              canEdit={
                engagement._access.isOwner ||
                engagement._access.myRole === "editor"
              }
              myRole={engagement._access.myRole}
              ownerEmail={engagement._access.ownerEmail}
              onCrossCheck={() => void handleCrossCheck(engagement)}
            />
          ))}
        </div>
      )}

      <DeleteConfirmDialog
        candidate={deleteCandidate}
        onCancel={() => setDeleteCandidate(null)}
        onConfirm={() => void confirmDelete()}
      />

      {shareTarget && (
        <ShareEngagementDialog
          open={shareTarget !== null}
          onOpenChange={(v) => !v && setShareTarget(null)}
          engagementId={shareTarget.id}
          engagementName={shareTarget.clientName}
          isOwner={shareTarget._access.isOwner}
        />
      )}
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────

function Header({ count }: { count: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div
      className="flex flex-wrap justify-between items-center mb-4 gap-3"
      data-testid="history-header"
    >
      <div>
        <h1 className="text-2xl font-semibold">{t("history.page.title")}</h1>
        <p
          className="text-sm text-muted-foreground"
          data-testid="history-count"
        >
          {t("history.page.count", { count })}
        </p>
      </div>
      <Button
        onClick={() => navigate("/engagements/new")}
        data-testid="history-new-engagement"
      >
        {t("history.action.newEngagement")}
      </Button>
    </div>
  );
}

// ─── Toolbar ───────────────────────────────────────────────────────────────

interface ToolbarProps {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  statusFilter: StatusFilter;
  onStatusChange: (v: StatusFilter) => void;
  currencyFilter: string;
  onCurrencyChange: (v: string) => void;
  uniqueCurrencies: string[];
  sortOrder: SortOrder;
  onSortChange: (v: SortOrder) => void;
  filtersActive: boolean;
  onClearFilters: () => void;
}

function Toolbar({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusChange,
  currencyFilter,
  onCurrencyChange,
  uniqueCurrencies,
  sortOrder,
  onSortChange,
  filtersActive,
  onClearFilters,
}: ToolbarProps) {
  const { t } = useTranslation();
  return (
    <Card className="p-4 mb-4" data-testid="history-toolbar">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("history.toolbar.search.placeholder")}
            className="pl-8 w-[260px]"
            data-testid="history-search"
          />
        </div>

        <div>
          <Label className="block text-xs font-medium mb-1 text-muted-foreground">
            {t("history.toolbar.status.label")}
          </Label>
          <Select
            value={statusFilter}
            onValueChange={(v) => onStatusChange(v as StatusFilter)}
          >
            <SelectTrigger
              className="w-[160px]"
              data-testid="history-filter-status"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">
                {t("history.toolbar.status.all")}
              </SelectItem>
              <SelectItem value="draft">
                {t("history.toolbar.status.draft")}
              </SelectItem>
              <SelectItem value="calculated">
                {t("history.toolbar.status.calculated")}
              </SelectItem>
              <SelectItem value="stale">
                {t("history.toolbar.status.stale")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="block text-xs font-medium mb-1 text-muted-foreground">
            {t("history.toolbar.currency.label")}
          </Label>
          <Select
            value={currencyFilter}
            onValueChange={(v) => onCurrencyChange(v)}
          >
            <SelectTrigger
              className="w-[120px]"
              data-testid="history-filter-currency"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">
                {t("history.toolbar.currency.all")}
              </SelectItem>
              {uniqueCurrencies.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="block text-xs font-medium mb-1 text-muted-foreground">
            {t("history.toolbar.sort.label")}
          </Label>
          <Select
            value={sortOrder}
            onValueChange={(v) => onSortChange(v as SortOrder)}
          >
            <SelectTrigger className="w-[160px]" data-testid="history-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">
                {t("history.toolbar.sort.newest")}
              </SelectItem>
              <SelectItem value="oldest">
                {t("history.toolbar.sort.oldest")}
              </SelectItem>
              <SelectItem value="clientName">
                {t("history.toolbar.sort.clientName")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filtersActive && (
          <Button
            variant="ghost"
            onClick={onClearFilters}
            data-testid="history-clear-filters"
          >
            <X className="mr-1 h-4 w-4" />
            {t("history.toolbar.clearFilters")}
          </Button>
        )}
      </div>
    </Card>
  );
}

// ─── EngagementCard ────────────────────────────────────────────────────────

interface EngagementCardProps {
  engagement: EngagementHistoryEntry;
  onOpen: () => void;
  onEditInfo: () => void;
  onEditContracts: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  canShare: boolean; // _access.isOwner || _access.myCanInvite
  onShare: () => void;
  isShared: boolean;
  canEdit: boolean; // isOwner || myRole === "editor"
  myRole: "viewer" | "editor" | null;
  ownerEmail: string | null;
  // TEMP: cross-check input export (MINI_FIX_BQ) — remove with the dev-tools folder + this button.
  onCrossCheck: () => void;
}

function EngagementCard({
  engagement,
  onOpen,
  onEditInfo,
  onEditContracts,
  onDuplicate,
  onDelete,
  canShare,
  onShare,
  isShared,
  canEdit,
  myRole,
  ownerEmail,
  onCrossCheck,
}: EngagementCardProps) {
  const { t } = useTranslation();
  const status = deriveEngagementStatus(engagement);
  const readOnly = isShared && !canEdit; // viewer
  const startDisplay = safeFormat(engagement.auditPeriodStart, "dd.MM.yyyy");
  const endDisplay = safeFormat(engagement.auditPeriodEnd, "dd.MM.yyyy");
  const lastCalc = engagement.lastResult?.calculatedAt;
  return (
    <Card
      className="p-4 hover:shadow-md transition-shadow"
      data-testid={`engagement-card-${engagement.id}`}
    >
      <div className="flex justify-between items-start mb-2 gap-3">
        <h2
          className="text-lg font-semibold truncate"
          title={engagement.clientName}
        >
          {engagement.clientName}
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          {isShared && (
            <Badge variant="secondary" data-testid="engagement-shared-badge">
              {t("history.shared.badge", {
                role:
                  myRole === "editor"
                    ? t("share.role.editor")
                    : t("share.role.viewer"),
              })}
            </Badge>
          )}
          <StatusBadge status={status} />
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        {startDisplay} — {endDisplay} · {engagement.functionalCurrency} ·{" "}
        {t("history.card.contractCount", { count: engagement.contractCount })}
      </p>
      {isShared && ownerEmail && (
        <p
          className="text-xs text-muted-foreground mt-1"
          data-testid="engagement-owner"
        >
          {t("history.shared.owner", { email: ownerEmail })}
        </p>
      )}
      {lastCalc ? (
        <p className="text-xs text-muted-foreground mt-1">
          {t("history.card.lastCalculated", {
            date: safeFormat(lastCalc, "dd.MM.yyyy HH:mm"),
          })}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground italic mt-1">
          {t("history.card.neverCalculated")}
        </p>
      )}
      {/* Phase BB5-2 — button hierarchy: primary "Aç" + two outline edits +
          two ghost-icon-only actions (duplicate, destructive delete). */}
      <TooltipProvider>
        <div className="flex flex-wrap items-center gap-2 mt-3 justify-end">
          {/* Phase BB6-4 — context-aware label: calculated engagements
              route to results (and say so); draft engagements still go to
              the contracts list. */}
          <Button
            variant="default"
            size="sm"
            onClick={onOpen}
            data-testid={`engagement-action-open-${engagement.id}`}
          >
            {engagement.lastResult
              ? t("history.action.openResults")
              : t("history.action.openContracts")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEditInfo}
            data-testid={`engagement-action-edit-info-${engagement.id}`}
          >
            {readOnly
              ? t("history.action.viewInfo")
              : t("history.action.editInfo")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEditContracts}
            data-testid={`engagement-action-edit-contracts-${engagement.id}`}
          >
            {readOnly
              ? t("history.action.viewContracts")
              : t("history.action.editContracts")}
          </Button>
          {/* Share — enabled for owner or can_invite member; disabled otherwise */}
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span wrapper so the tooltip works even when the button is disabled */}
              <span tabIndex={0}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onShare}
                  disabled={!canShare}
                  className="gap-1"
                  data-testid={`engagement-action-share-${engagement.id}`}
                >
                  <Share2 className="h-4 w-4" aria-hidden="true" />
                  {t("share.button")}
                </Button>
              </span>
            </TooltipTrigger>
            {!canShare && (
              <TooltipContent>{t("share.noPermission")}</TooltipContent>
            )}
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span wrapper so the tooltip works even when the button is disabled */}
              <span tabIndex={0}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDuplicate}
                  disabled={readOnly}
                  aria-label={t("history.action.duplicate")}
                  data-testid={`engagement-action-duplicate-${engagement.id}`}
                >
                  <Copy className="h-4 w-4" aria-hidden="true" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {readOnly
                ? t("history.shared.noEditPermission")
                : t("history.action.duplicate")}
            </TooltipContent>
          </Tooltip>
          {/* TEMP: cross-check input export (MINI_FIX_BQ) — remove with the dev-tools folder + this button. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onCrossCheck}
            data-testid={`engagement-action-crosscheck-${engagement.id}`}
          >
            🔬 Cross-check Excel
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span wrapper so the tooltip works even when the button is disabled */}
              <span tabIndex={0}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  disabled={readOnly}
                  aria-label={t("history.action.delete")}
                  data-testid={`engagement-action-delete-${engagement.id}`}
                >
                  <Trash2
                    className="h-4 w-4 text-destructive"
                    aria-hidden="true"
                  />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {readOnly
                ? t("history.shared.noEditPermission")
                : t("history.action.delete")}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </Card>
  );
}

function safeFormat(iso: string, fmt: string): string {
  try {
    const date = parseISO(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return format(date, fmt);
  } catch {
    return iso;
  }
}

// ─── DeleteConfirmDialog ──────────────────────────────────────────────────

interface DeleteConfirmDialogProps {
  candidate: EngagementHistoryEntry | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeleteConfirmDialog({
  candidate,
  onCancel,
  onConfirm,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation();
  const open = candidate !== null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid="delete-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{t("history.delete.confirm.title")}</DialogTitle>
          <DialogDescription>
            {t("history.delete.confirm.body", {
              name: candidate?.clientName ?? "",
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="delete-confirm-cancel"
          >
            {t("history.delete.confirm.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            data-testid="delete-confirm-delete"
          >
            {t("history.delete.confirm.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Empty states + skeleton ───────────────────────────────────────────────

function EmptyState() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div
      className="text-center py-16 max-w-md mx-auto"
      data-testid="history-empty-state"
    >
      <p className="text-muted-foreground mb-4">
        {t("history.emptyState.noEngagements")}
      </p>
      <Button
        onClick={() => navigate("/engagements/new")}
        data-testid="history-empty-state-cta"
      >
        {t("history.action.newEngagement")}
      </Button>
    </div>
  );
}

function FilteredEmptyState() {
  const { t } = useTranslation();
  return (
    <p
      className="text-center italic text-muted-foreground py-8"
      data-testid="history-filtered-empty-state"
    >
      {t("history.emptyState.filtered")}
    </p>
  );
}

function SkeletonPlaceholder() {
  return (
    <div
      className="max-w-5xl mx-auto space-y-3"
      data-testid="history-skeleton"
    >
      <div className="h-8 w-48 bg-muted/50 rounded animate-pulse" />
      <div className="h-20 w-full bg-muted/30 rounded animate-pulse" />
      <div className="h-32 w-full bg-muted/30 rounded animate-pulse" />
      <div className="h-32 w-full bg-muted/30 rounded animate-pulse" />
    </div>
  );
}
